import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom'; 
import { Paper, Typography, Box, Button, Autocomplete, Divider, TextField, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import {
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart
} from 'recharts';
import * as API from '../../api';
import { formatCurrency } from '../../utils/currencyUtils';
import { startOfMonth, endOfMonth, startOfYear, endOfYear, format } from 'date-fns';
import CustomDatePicker from '../CustomDatePicker';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF5722'];

const CustomLabel = ({ title }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
    {title}
  </Typography>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper sx={{ p: 1.5, border: '1px solid #eee', boxShadow: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{label}</Typography>
        {payload.map((entry, index) => (
          <Typography key={index} variant="body2" sx={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </Typography>
        ))}
      </Paper>
    );
  }
  return null;
};

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius * 1.2;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="black" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="11px" fontWeight="bold">
      {`${name} (${(percent * 100).toFixed(0)}%)`}
    </text>
  );
};

const PerformanceTab = forwardRef((props, ref) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [uiStartDate, setUiStartDate] = useState(
    searchParams.get('start') || format(startOfMonth(new Date()), 'yyyy-MM-dd')
  );
  const [uiEndDate, setUiEndDate] = useState(
    searchParams.get('end') || format(endOfMonth(new Date()), 'yyyy-MM-dd')
  );
  const [filterMode, setFilterMode] = useState(
    searchParams.get('mode') || 'CUSTOM'
  );
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [stats, setStats] = useState(null);

  useImperativeHandle(ref, () => ({
    resetFilters: () => {
      const defaultStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
      const defaultEnd = format(endOfMonth(new Date()), 'yyyy-MM-dd');
      setUiStartDate(defaultStart);
      setUiEndDate(defaultEnd);
      setSelectedUser(null);
      setFilterMode('CUSTOM');
      setSearchParams({ tab: 'OVERVIEW' });
      const defaultParams = { start_date: defaultStart, end_date: defaultEnd, sale_owner: 'ALL' };
      API.getManagementStats(defaultParams).then(res => setStats(res.data)).catch(console.error);
    }
  }));

  useEffect(() => {
    const initData = async () => {
      try {
        const userRes = await API.fetchUsers();
        setUserOptions(userRes.data);
        const urlSaleOwnerId = searchParams.get('sale_owner');
        if (urlSaleOwnerId && urlSaleOwnerId !== 'ALL') {
          const found = userRes.data.find(u => u.id.toString() === urlSaleOwnerId);
          if (found) setSelectedUser(found);
        }
        fetchStats(userRes.data);
      } catch (error) { console.error(error); }
    };
    initData();
  }, []);

  const fetchStats = (loadedUsers = []) => {
    let apiStart = uiStartDate;
    let apiEnd = uiEndDate;
    if (filterMode === 'ALL') { apiStart = ''; apiEnd = ''; }
    else if (filterMode === 'YEAR') {
      const now = new Date();
      apiStart = format(startOfYear(now), 'yyyy-MM-dd');
      apiEnd = format(endOfYear(now), 'yyyy-MM-dd');
    }
    const currentOwnerId = selectedUser ? selectedUser.id : (searchParams.get('sale_owner') || 'ALL');
    const resolvedOwnerId = currentOwnerId === 'ALL' ? 'ALL' : currentOwnerId;
    const params = { start_date: apiStart, end_date: apiEnd, sale_owner: resolvedOwnerId };
    const urlParams = { tab: 'OVERVIEW', mode: filterMode };
    if (filterMode === 'CUSTOM') { urlParams.start = apiStart; urlParams.end = apiEnd; }
    if (resolvedOwnerId !== 'ALL') { urlParams.sale_owner = resolvedOwnerId; }
    setSearchParams(urlParams);
    
    API.getManagementStats(params).then(res => setStats(res.data)).catch(err => console.error(err));
  };

  const handleCheckboxChange = (mode) => {
    setFilterMode(prev => prev === mode ? 'CUSTOM' : mode);
  };

// Hàm điều hướng khi bấm nút Xem chi tiết
  const handleViewDetails = (type) => {
    const params = new URLSearchParams();
    
    // 1. Đồng bộ thời gian & Sale (Giữ nguyên)
    if (filterMode === 'CUSTOM') {
      params.append('start', uiStartDate);
      params.append('end', uiEndDate);
    } else if (filterMode === 'ALL') {
      params.append('all_time', 'true');
    } else if (filterMode === 'YEAR') {
       const now = new Date();
       const startYear = format(startOfYear(now), 'yyyy-MM-dd');
       const endYear = format(endOfYear(now), 'yyyy-MM-dd');
       params.append('start', startYear);
       params.append('end', endYear);
    }
    
    if (selectedUser) params.append('sale_owner', selectedUser.id);

    // 2. Xử lý logic từng loại
    if (type === 'PAYABLE') {
      params.append('tab', 'PAYABLE');
      params.append('status', 'UNPAID,PAID'); 
    } else if (type === 'GENERAL_EXPENSE') {
      params.append('tab', 'ACCOUNTING'); 
      params.append('subtab', 'GENERAL_EXPENSES');
    } else {
      // --- ĐIỀU HƯỚNG SANG TAB BOOKING ---
      params.append('tab', 'BOOKING');
      
      if (type === 'REVENUE') params.append('payment_status', 'PAID');
      params.append('bk_status', 'PENDING,CONFIRMED,CLOSED,LOST,CANCELLED');
      params.append('liability', 'DEBT_TOUR,DEBT_TRANSPORT,DEBT_ROOM,PAID');
    }
    
    navigate(`/management?${params.toString()}`);
  };

  if (!stats) return <Typography sx={{p:3}}>Đang tải dữ liệu thống kê...</Typography>;
  const { overview, pie_data, line_data } = stats;
  const rowStyle = { display: 'flex', gap: 2, alignItems: 'flex-end', width: '100%' };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ px: 3, py: 2, mb: 3, borderRadius: 2, width: '100%' }}>
        <Box sx={{ ...rowStyle, alignItems: 'flex-start' }}>
          <Box>
            <CustomLabel title="Lọc nhanh" />
            <FormGroup row sx={{ height: '40px', alignItems: 'center' }}>
              <FormControlLabel control={<Checkbox checked={filterMode === 'ALL'} onChange={() => handleCheckboxChange('ALL')} size="small" sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' } }} />} label={<Typography variant="body2">Toàn thời gian</Typography>} sx={{ mr: 2 }} />
              <FormControlLabel control={<Checkbox checked={filterMode === 'YEAR'} onChange={() => handleCheckboxChange('YEAR')} size="small" sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' } }} />} label={<Typography variant="body2">Năm nay</Typography>} sx={{ mr: 0 }} />
            </FormGroup>
          </Box>
          <Box flex={1}>
            <CustomLabel title="Từ ngày" />
            <CustomDatePicker value={uiStartDate} onChange={e => setUiStartDate(e.target.value)} disabled={filterMode !== 'CUSTOM'} />
          </Box>
          <Box flex={1}>
            <CustomLabel title="Đến ngày" />
            <CustomDatePicker value={uiEndDate} onChange={e => setUiEndDate(e.target.value)} disabled={filterMode !== 'CUSTOM'} />
          </Box>
          <Box flex={1}>
            <CustomLabel title="Nhân viên phụ trách" />
            <Autocomplete size="small" fullWidth style={{marginBottom: '0px !important'}} options={userOptions} getOptionLabel={(opt) => `${opt.last_name} ${opt.first_name}`} value={selectedUser} onChange={(e, val) => setSelectedUser(val)} renderInput={(params) => <TextField {...params} placeholder="Chọn nhân viên..." />} />
          </Box>
          <Box>
            <Button variant="contained" fullWidth onClick={() => fetchStats()} sx={{ bgcolor: '#007373', '&:hover': { bgcolor: '#005c5c' }, height: '40px', px: 3, borderRadius: 2, whiteSpace: 'nowrap', marginTop: '24px' }}>Áp dụng bộ lọc</Button>
          </Box>
        </Box>
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ ...rowStyle, gap: 3, alignItems: 'flex-start' }}>
          <StatCard 
            title="TỔNG BOOKING" 
            value={overview.count} 
            color="#1976d2" 
            onClick={() => handleViewDetails('BOOKING')} 
          />
          <StatCard 
            title="DOANH THU" 
            value={formatCurrency(overview.revenue)} 
            color="#2e7d32" 
            onClick={() => handleViewDetails('REVENUE')} 
          />
          <StatCard 
            title="TỔNG CÔNG NỢ" 
            value={formatCurrency(overview.cost)} 
            color="#d32f2f" 
            onClick={() => handleViewDetails('PAYABLE')} 
          />
          <StatCard 
            title="LỢI NHUẬN (EST)" 
            value={formatCurrency(overview.profit)} 
            color="#ed6c02" 
            onClick={() => handleViewDetails('BOOKING')} 
          />
          <StatCard 
            title="CHI PHÍ CHUNG" 
            value={formatCurrency(overview.general_expense || 0)} 
            color="#8e24aa" // Màu tím hoặc màu khác tùy chọn
            onClick={() => handleViewDetails('GENERAL_EXPENSE')} 
          />
        </Box>
      </Paper>

      <Paper sx={{ px: 3, py: 2, borderRadius: 2, width: '100%' }}>
        <Box sx={{ ...rowStyle, gap: 4, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#555', textTransform: 'uppercase', mb: 2 }}>TỶ TRỌNG NGUỒN KHÁCH</Typography>
            <Box sx={{ height: 350, width: '100%', display: 'flex', justifyContent: 'center' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pie_data} dataKey="count" nameKey="source" cx="50%" cy="50%" innerRadius={60} outerRadius={100} fill="#8884d8" label={renderCustomizedLabel} paddingAngle={2}>
                    {pie_data.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <RechartsTooltip />
                  <Legend verticalAlign="bottom" height={36}/>
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Box>
          <Box sx={{ flex: 2 }}>
            <Typography variant="subtitle1" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#555', textTransform: 'uppercase', mb: 2 }}>XU HƯỚNG DOANH THU (THEO THỜI GIAN)</Typography>
            <Box sx={{ height: 350, width: '100%' }}>
              <ResponsiveContainer>
                <AreaChart data={line_data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <defs><linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#007373" stopOpacity={0.8}/><stop offset="95%" stopColor="#007373" stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} tickFormatter={(val) => val >= 1000000 ? `${val/1000000}M` : val} width={80} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area type="monotone" dataKey="Doanh thu" stroke="#007373" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" activeDot={{ r: 6, strokeWidth: 0 }} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
});

const StatCard = ({ title, value, color, onClick }) => (
  <Box sx={{ flex: 1, height: '100%' }}>
    <Paper
      elevation={0} variant="outlined"
      sx={{
        p: 2, textAlign: 'center',
        borderTop: `4px solid ${color}`, borderRadius: 2,
        bgcolor: '#fff', height: '100%',
        display: 'flex', flexDirection: 'column', justifyContent: 'center'
      }}
    >
      <Typography color="textSecondary" variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 'bold', fontSize: '0.75rem' }}>{title}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 'bold', color: color, mt: 1 }}>{value}</Typography>
      
      {/* Nút Xem chi tiết */}
      <Button 
        size="small" 
        endIcon={<ArrowForwardIcon sx={{ fontSize: '1rem !important' }} />}
        onClick={onClick}
        sx={{ 
          mt: 1, 
          color: color, 
          textTransform: 'none', 
          fontSize: '0.75rem',
          '&:hover': { bgcolor: `${color}10` }
        }}
      >
        Xem chi tiết
      </Button>
    </Paper>
  </Box>
);

export default PerformanceTab;